import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, clearToken, setToken, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anon, object = user
  const [user, setUser] = useState(null);
  const idleTimerRef = useRef(null);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    clearToken();
    setUser(false);
  }, []);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    // 60 min inactivity
    idleTimerRef.current = setTimeout(() => {
      logout();
    }, 60 * 60 * 1000);
  }, [logout]);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      resetIdleTimer();
    } catch (e) {
      setUser(false);
    }
  }, [resetIdleTimer]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  useEffect(() => {
    if (!user || user === false) return;
    const events = ["mousemove", "keydown", "click", "scroll"];
    const handler = () => resetIdleTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, resetIdleTimer]);

  const loginStep1 = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // If backend skipped OTP (trusted device), complete login now
    if (data && data.requires_otp === false && data.token) {
      setToken(data.token);
      setUser(data.user);
      resetIdleTimer();
    }
    return data; // { requires_otp: bool, ... }
  };

  const verifyOtp = async (email, code, rememberDevice = false) => {
    const { data } = await api.post("/auth/verify-otp", { email, code, remember_device: rememberDevice });
    if (data.token) setToken(data.token);
    setUser(data.user);
    resetIdleTimer();
    return data.user;
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loginStep1, verifyOtp, logout, hasToken: !!getToken() }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
